import React, { useState, useEffect } from 'react';
import { X, MapPin, User, Mail } from 'lucide-react';
import { collection, addDoc, serverTimestamp, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from './StripeWebhookHandler';
import ShippingSelector from './ShippingSelector';
import PaymentForm from './PaymentForm';
import './CheckoutForm.css';

// Load Stripe
const stripePromise = getStripe();

function CheckoutForm({ cart, total, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shipping, setShipping] = useState({ method: null, cost: 0 });
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [showPayment, setShowPayment] = useState(false);

  // Load customer data from Firestore when component mounts
  useEffect(() => {
    loadCustomerData();
  }, []);

  const loadCustomerData = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log('No user logged in, using empty form');
        return;
      }

      console.log('Loading customer data for user:', user.email);
      
      // Query customers collection by email
      const customerQuery = query(
        collection(db, 'customers'),
        where('email', '==', user.email)
      );
      
      const customerSnapshot = await getDocs(customerQuery);
      
      if (!customerSnapshot.empty) {
        const customerDoc = customerSnapshot.docs[0];
        const customerData = customerDoc.data();
        
        console.log('Found customer data:', customerData);
        
        // Pre-fill form with customer data
        setFormData({
          name: customerData.name || '',
          email: customerData.email || user.email || '',
          phone: customerData.phone || '',
          address: customerData.address?.line1 || '',
          address2: customerData.address?.line2 || '',
          city: customerData.address?.city || '',
          state: customerData.address?.state || '',
          zipCode: customerData.address?.postalCode || '',
          country: customerData.address?.country || 'US',
          notes: ''
        });
        
        console.log('Form pre-filled with customer data');
      } else {
        console.log('No customer data found, using user email');
        // At least set the email from Firebase Auth
        setFormData(prev => ({
          ...prev,
          email: user.email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
      // Keep empty form on error
    }
  };

  // Calculate subtotal (cart total without shipping)
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  // NYC Sales Tax (8.875% for NYC)
  const nycTaxRate = 0.08875;
  const tax = subtotal * nycTaxRate;
  
  const finalTotal = subtotal + shipping.cost + tax;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'MX', name: 'Mexico' }
  ];

  const createOrder = async () => {
    try {
      // Create order in Firestore
      const orderData = {
        customer: formData,
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          category: item.category,
          price: item.price,
          quantity: item.quantity
        })),
        subtotal: subtotal,
        tax: tax,
        taxRate: nycTaxRate,
        shipping: {
          type: shipping.type,
          method: shipping.method?.name || 'Standard',
          carrier: shipping.method?.carrier || 'USPS',
          cost: shipping.cost,
          estimatedDays: shipping.method?.estimatedDays || '3-5 business days',
          location: shipping.location ? {
            name: shipping.location.name,
            address: shipping.location.address
          } : null
        },
        total: finalTotal,
        status: 'pending',
        paymentStatus: 'pending',
        createdAt: serverTimestamp()
      };

      const orderRef = await addDoc(collection(db, 'orders'), orderData);
      return orderRef.id;
    } catch (err) {
      console.error('Error creating order:', err);
      throw err;
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      console.log('💰 Payment successful, creating customer account first...');
      
      // Create customer account FIRST
      let customerId = paymentData.customer?.id;
      if (!customerId) {
        console.log('👤 Creating customer account...');
        
        // Check if customer already exists in Firestore
        const { collection, query, where, getDocs, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        const customerQuery = query(
          collection(db, 'customers'),
          where('email', '==', formData.email)
        );
        
        const existingCustomer = await getDocs(customerQuery);
        
        if (!existingCustomer.empty) {
          // Use existing customer
          const customerDoc = existingCustomer.docs[0];
          customerId = customerDoc.id;
          console.log('✅ Using existing customer:', customerId);
        } else {
          // Create new customer
          const customerData = {
            email: formData.email,
            name: formData.name,
            phone: formData.phone,
            address: {
              line1: formData.address,
              line2: formData.address2 || '',
              city: formData.city,
              state: formData.state,
              postalCode: formData.zipCode,
              country: formData.country,
            },
            createdAt: serverTimestamp(),
            totalOrders: 0,
            totalSpent: 0,
            lastOrderAt: null,
            paymentMethods: [],
            status: 'active'
          };
          
          const newCustomerRef = doc(collection(db, 'customers'));
          await setDoc(newCustomerRef, customerData);
          customerId = newCustomerRef.id;
          console.log('✅ Customer account created:', customerId);
        }
      }
      
      // Now create the order with customer ID
      console.log('📋 Creating order with customer ID...');
      const newOrderId = await createOrder();
      console.log('📋 Order created:', newOrderId);
      setOrderId(newOrderId);

      // Update order with payment and customer info
      const orderRef = doc(db, 'orders', newOrderId);
      await updateDoc(orderRef, {
        paymentStatus: 'succeeded',
        status: 'confirmed',
        paymentIntentId: paymentData.paymentIntent.id,
        customerId: customerId,
        updatedAt: serverTimestamp()
      });

      // Update customer stats with the new order
      console.log('📊 Updating customer stats with new order...');
      try {
        const { doc, updateDoc, serverTimestamp, collection, addDoc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        // Update customer document
        const customerRef = doc(db, 'customers', customerId);
        const customerDoc = await getDoc(customerRef);
        
        if (customerDoc.exists()) {
          const customerData = customerDoc.data();
          await updateDoc(customerRef, {
            totalOrders: (customerData.totalOrders || 0) + 1,
            totalSpent: (customerData.totalSpent || 0) + finalTotal,
            lastOrderAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          
          // Add order to customer's order history subcollection
          await addDoc(collection(customerRef, 'orders'), {
            orderId: newOrderId,
            amount: finalTotal,
            status: 'confirmed',
            createdAt: serverTimestamp()
          });
          
          console.log('✅ Customer stats updated');
        }
      } catch (statsError) {
        console.warn('⚠️ Failed to update customer stats:', statsError);
      }

      console.log('✅ Order and customer created successfully');
      setOrderPlaced(true);
      setShowPayment(false);
    } catch (err) {
      console.error('❌ Error handling payment success:', err);
      setError('Payment succeeded but failed to create order. Please contact support.');
    }
  };

  const handlePaymentError = (error) => {
    setError(error.message || 'Payment failed. Please try again.');
  };

  const proceedToPayment = async () => {
    console.log('🚀 proceedToPayment called');
    console.log('📦 Shipping:', shipping);
    console.log('📝 Form data:', formData);
    
    // Allow pickup without a shipping method
    if (!shipping.method && shipping.type !== 'pickup') {
      console.log('❌ No shipping method selected');
      setError('Please select a shipping method');
      return;
    }

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.zipCode) {
      console.log('❌ Missing required fields');
      setError('Please fill in all required fields');
      return;
    }

    console.log('✅ Validation passed, showing payment form...');
    setLoading(true);
    setError('');

    try {
      // Don't create order yet - just show payment form
      setShowPayment(true); // Show payment form
      console.log('💳 Show payment form set to true');
    } catch (err) {
      console.error('❌ Error showing payment form:', err);
      setError('Failed to show payment form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-overlay" onClick={onClose}>
      <div className="checkout-panel" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-header">
          <h2>Checkout</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="checkout-content">
          <form className="checkout-form">
            {error && <div className="error-message">{error}</div>}
            
            {/* Debug logs */}
            {console.log('🔍 Render - showPayment:', showPayment, 'orderPlaced:', orderPlaced, 'loading:', loading)}
            
            {/* Success Message */}
            {orderPlaced && !error && (
              <div className="success-message">
                <h3>🎉 Order Placed Successfully!</h3>
                <p>Your order has been confirmed and will be processed shortly.</p>
                <p>Order ID: <strong>{orderId}</strong></p>
                <p>You will receive a confirmation email shortly.</p>
              </div>
            )}

            <div className="form-section">
              <h3><User size={20} /> Contact Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(868) 123-4567"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3><MapPin size={20} /> Delivery Address</h3>
              
              <div className="form-group">
                <label htmlFor="country">Country</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="address">Street Address</label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="address2">Apartment, suite, unit, building, floor, etc.</label>
                <input
                  id="address2"
                  name="address2"
                  type="text"
                  value={formData.address2}
                  onChange={handleChange}
                  placeholder="Apt 4B"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="New York"
                    required
                  />
                </div>

                {formData.country === 'US' && (
                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="zipCode">
                    {formData.country === 'US' ? 'ZIP Code' : 'Postal Code'}
                  </label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    value={formData.zipCode}
                    onChange={handleChange}
                    placeholder={formData.country === 'US' ? '10001' : 'ABC123'}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Delivery Notes (Optional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any special instructions for delivery..."
                />
              </div>
            </div>

        {!showPayment ? (
          <>
            <ShippingSelector 
              subtotal={subtotal} 
              onShippingChange={setShipping} 
            />

            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="summary-item">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-item">
                <span>Tax (NYC 8.875%)</span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="summary-item">
                <span>Shipping</span>
                <span>
                  {shipping.cost === 0 ? 'FREE' : `$${shipping.cost.toFixed(2)}`}
                </span>
              </div>
              {shipping.method && (
                <div className="summary-item shipping-method">
                  <span>Delivery Method</span>
                  <span>{shipping.method.name} {shipping.method.estimatedDays && `(${shipping.method.estimatedDays})`}</span>
                </div>
              )}
              {shipping.location && (
                <div className="summary-item pickup-location">
                  <span>Pickup Location</span>
                  <span>{shipping.location.name}</span>
                </div>
              )}
              <div className="summary-item total">
                <span>Total</span>
                <span>${finalTotal.toFixed(2)}</span>
              </div>
            </div>

            <button 
              type="button" 
              className="place-order-btn"
              onClick={proceedToPayment}
              disabled={loading || (!shipping.method && shipping.type !== 'pickup')}
            >
              {loading ? 'Creating Order...' : 'Proceed to Payment'}
            </button>
          </>
        ) : showPayment ? (
          <Elements stripe={stripePromise}>
            <PaymentForm
              orderData={{
                customer: formData,
                items: cart,
                subtotal: subtotal,
                tax: tax,
                taxRate: nycTaxRate,
                shipping: shipping,
                total: finalTotal,
                orderId: null // Order will be created after payment
              }}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
              loading={loading}
              setLoading={setLoading}
            />
          </Elements>
        ) : null}

          </form>
        </div>
      </div>
    </div>
  );
}

export default CheckoutForm;
